import * as React from 'react';

import {
  enumDocumentType,
  enumUnit,
  Unit,
  Weight,
  WeightInput,
} from '@rematter/frontend-lib-api-client';
import { Button, DialogMain } from '@rematter/ui-web';
import {
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogOverlay,
} from '@rematter/ui-web';
import { useSetAtom, useAtomValue } from 'jotai';
import {
  LucidePrinter,
  LucideDownload,
  LucideMail,
  LucideFileCheck2,
} from 'lucide-react';

import { MaterialsReconciliation } from '../_shared/MaterialsReconciliation';
import { InvoiceDetails } from '../_shared/MaterialsReconciliation/InvoiceDetails';
import { ReceivingTicketInfo } from '../_shared/MaterialsReconciliation/ReceivingTicketInfo';
import { ReconciliationSummary } from '../_shared/MaterialsReconciliation/ReconciliationSummary';
import { StepIndicator } from '../_shared/StepIndicator';

import {
  $resetMaterialsReconciliationFormToFullReconciliation,
  $materialsReconciliationForm,
  $deductionTypes,
  $loadReconciliation,
  $updateLoadReconciliation,
  $loadId,
  $loadDetail,
} from '~/modules/trade/_state/loadReconciliation.state';
import { usePrintWithBrowser } from '~/modules/trade/hooks/usePrintWithBrowser';
import {
  DefaultTranslations,
  useTranslations,
} from '~/modules/trade/i18n/useTranslations';
import { WeightUnitType } from '~/modules/trade/types/load';
import { LoadRecord } from '~/modules/trade/types/load';
import { toMoneyInput, zeroMoney } from '~/modules/trade/utils/money';
import {
  $defaultUnitSystemUnit,
  $salesDefaultWeightDisplayUnit,
} from '~/shared/state/settings.state';

// Convert Weight object to WeightInput
const convertWeightQuantityToWeightInput = (
  weightQuantity: Weight | null | undefined,
): WeightInput | undefined => {
  if (!weightQuantity) {
    return undefined;
  }
  return {
    value: weightQuantity.value,
    unit: weightQuantity.unit,
  };
};

interface LoadReconciliationWizardProps {
  loadId?: string;
  loadNumber?: string;
  isOpen: boolean;
  onClose: () => void;
  onReconcile: () => Promise<void>;
  onSuccess?: () => void;
  skipMaterialsReconciliation?: boolean;
}

interface Step {
  number: number;
  title: string;
}

const getWizardSteps = (
  t: DefaultTranslations,
  skipMaterialsReconciliation: boolean,
): Step[] => {
  const steps: Step[] = [];

  if (!skipMaterialsReconciliation) {
    steps.push({
      number: 1,
      title:
        t.loadStatusTransition.reconciliation.steps.materialsReconciliation,
    });
  }

  steps.push(
    {
      number: skipMaterialsReconciliation ? 1 : 2,
      title: t.loadStatusTransition.reconciliation.steps.receivingTicketInfo,
    },
    {
      number: skipMaterialsReconciliation ? 2 : 3,
      title: t.loadStatusTransition.reconciliation.steps.invoiceDetails,
    },
  );

  return steps;
};

export const LoadReconciliationWizard: React.FC<
  LoadReconciliationWizardProps
> = ({
  loadId,
  loadNumber,
  isOpen,
  onClose,
  onReconcile,
  onSuccess,
  skipMaterialsReconciliation = false,
}) => {
  const t = useTranslations();
  const resetFormToFullReconciliation = useSetAtom(
    $resetMaterialsReconciliationFormToFullReconciliation,
  );
  const formState = useAtomValue($materialsReconciliationForm);
  const [currentStep, setCurrentStep] = React.useState(1);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isReconciled, setIsReconciled] = React.useState(false);
  const [_showEmailInput, setShowEmailInput] = React.useState(false);

  const [isPrinting, setIsPrinting] = React.useState(false);
  const [isDownloading, setIsDownloading] = React.useState(false);

  // Mutation atom
  const updateReconciliationMutation = useSetAtom($updateLoadReconciliation);

  const { data } = useAtomValue($loadDetail);
  const load = data?.load;
  const { data: loadReconciliationData } = useAtomValue($loadReconciliation);

  const loadReconciliation = loadReconciliationData;

  // Get deduction types from atom
  const deductionTypesData = useAtomValue($deductionTypes);
  const deductionTypes = deductionTypesData?.data;

  // Weight unit settings - use local state so MaterialsReconciliation and ReconciliationFooter share the same state
  const facilityDefaultUnit = useAtomValue($defaultUnitSystemUnit);
  const defaultWeightDisplayUnit = useAtomValue($salesDefaultWeightDisplayUnit);
  const [weightUnit, setWeightUnit] = React.useState<WeightUnitType>(
    defaultWeightDisplayUnit,
  );

  // Update local state when settings change
  React.useEffect(() => {
    setWeightUnit(defaultWeightDisplayUnit);
  }, [defaultWeightDisplayUnit]);

  // Set loadId in the reconciliation atom when loadId changes
  const setLoadId = useSetAtom($loadId);
  React.useEffect(() => {
    if (loadId) {
      setLoadId(loadId);
    }
  }, [loadId, setLoadId]);

  // Initialize form when reconciliation data is loaded
  React.useEffect(() => {
    const loadForForm = {
      lineItems: load?.lineItems ?? [],
      salesOrder: { paymentTerm: load?.salesOrder?.paymentTerm },
    };

    if (load && loadReconciliation) {
      resetFormToFullReconciliation(loadForForm, loadReconciliation);
    } else if (load && !loadReconciliation) {
      resetFormToFullReconciliation(loadForForm, undefined);
    }
  }, [load, loadReconciliation, resetFormToFullReconciliation]);

  const wizardSteps = getWizardSteps(t, skipMaterialsReconciliation);

  const maxStep = skipMaterialsReconciliation ? 2 : 3;
  const isLastStep = currentStep === maxStep;
  const isFirstStep = currentStep === 1;

  const handleClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const handleNext = React.useCallback(async () => {
    setCurrentStep((prev) => Math.min(prev + 1, maxStep));
  }, [maxStep]);

  const handleBack = React.useCallback(() => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  }, []);

  const handleReconcile = React.useCallback(async () => {
    if (!loadId || !loadReconciliation?.id) {
      console.error('Load ID or reconciliation ID is missing');
      return;
    }

    setIsSubmitting(true);
    try {
      const receivedLineItems =
        formState.receivedLineItems.length > 0
          ? formState.receivedLineItems.map((item) => ({
              id: item.id?.startsWith('temp') ? undefined : item.id,
              itemId: item.itemId,
              loadLineItemId: item.loadLineItemId,
              materialType: item.materialType ?? 'Raw',
              grossWeight: item.grossWeight
                ? {
                    value: item.grossWeight.value,
                    unit: item.grossWeight.unit as Unit,
                  }
                : { value: 0, unit: enumUnit.Pounds },
              tareWeight: item.tareWeight
                ? {
                    value: item.tareWeight.value,
                    unit: item.tareWeight.unit as Unit,
                  }
                : { value: 0, unit: enumUnit.Pounds },
              unitPrice: toMoneyInput(item.unitPrice),
              pricingUnit: item.pricingUnit
                ? item.pricingUnit
                : enumUnit.Pounds,
              notes: item.notes,
            }))
          : undefined;

      const deductions =
        formState.deductions.length > 0
          ? formState.deductions.map((deduction) => ({
              id: deduction.id?.startsWith('temp') ? undefined : deduction.id,
              receivedLineItemId: deduction.receivedLineItemId,
              deductionTypeId: deduction.deductionTypeId,
              priceQuantity: deduction.priceQuantity
                ? toMoneyInput(deduction.priceQuantity)
                : undefined,
              weightQuantity: convertWeightQuantityToWeightInput(
                deduction.weightQuantity,
              ),
            }))
          : undefined;

      const freightCharges =
        formState.freightCharges.length > 0
          ? formState.freightCharges.map((charge) => ({
              id: charge.id?.startsWith('temp') ? undefined : charge.id,
              hauler: charge.hauler,
              freightLane: charge.freightLane,
              deliveryNumber: charge.deliveryNumber,
              accruedAmount: charge.accruedAmount
                ? toMoneyInput(charge.accruedAmount)
                : zeroMoney(),
              chargebackAmount: charge.chargebackAmount
                ? toMoneyInput(charge.chargebackAmount)
                : zeroMoney(),
              notes: charge.notes ?? '',
            }))
          : undefined;

      // Update the reconciliation with form data
      await updateReconciliationMutation({
        loadId: loadId,
        receivedLineItems,
        deductions,
        freightCharges,
        ticketNumber: formState.ticketNumber,
        loadReceivedDate: formState.loadReceivedDate,
        ticketIssueDate: formState.ticketIssueDate,
        millReceivedWeightPostDate: formState.millReceivedWeightPostDate,
        externalNotes: formState.externalNotes,
        internalNotes: formState.internalNotes,
        invoiceType: formState.invoiceType,
      });

      // Call the original reconcile function
      await onReconcile();

      // Set reconciled state to show success screen
      setIsReconciled(true);

      onSuccess?.();
    } catch (error) {
      // Error is already handled by the mutation
      console.error('Reconciliation failed:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    loadId,
    loadReconciliation?.id,
    formState,
    updateReconciliationMutation,
    onReconcile,
    onSuccess,
  ]);

  const { print: printWithBrowser } = usePrintWithBrowser();

  const handlePrint = React.useCallback(async () => {
    try {
      setIsPrinting(true);
      await printWithBrowser({
        documents: [
          {
            documentType: enumDocumentType.Invoice,
            invoiceId: formState.invoiceId ?? undefined,
            loadId: loadId ?? undefined,
          },
        ],
      });
    } finally {
      setIsPrinting(false);
    }
  }, [formState.invoiceId, loadId, printWithBrowser]);

  const handleDownload = React.useCallback(async () => {
    try {
      setIsDownloading(true);
      await printWithBrowser({
        documents: [
          {
            documentType: enumDocumentType.Invoice,
            invoiceId: formState.invoiceId ?? undefined,
            loadId: loadId ?? undefined,
          },
        ],
        action: 'download',
      });
    } finally {
      setIsDownloading(false);
    }
  }, [formState.invoiceId, loadId, printWithBrowser]);

  const handleSendEmail = React.useCallback(() => {
    setShowEmailInput(true);
  }, []);

  const handleCloseSuccess = React.useCallback(() => {
    setIsReconciled(false);
    onClose();
  }, [onClose]);

  const renderSuccessScreen = () => (
    <div className="flex flex-col items-center justify-center px-8 py-16 text-center">
      <div className="mb-6">
        <LucideFileCheck2 className="mx-auto mb-4 size-16 text-success opacity-50" />
      </div>

      <h2 className="mb-2 text-2xl font-semibold text-neutral/90">
        {t.loadStatusTransition.wizard.success.title(loadNumber ?? '')}
      </h2>

      <p className="mb-8 text-neutral/60">
        {t.loadStatusTransition.wizard.success.description}
      </p>

      <div className="mb-8 flex gap-4">
        <Button
          variant="outline"
          onPress={handlePrint}
          className="flex items-center gap-2"
          isLoading={isPrinting}
        >
          {!isPrinting && <LucidePrinter className="size-4" />}
          {isPrinting
            ? t.loadStatusTransition.wizard.actions.printingInvoice
            : t.loadStatusTransition.wizard.actions.print}
        </Button>

        <Button
          variant="outline"
          onPress={handleDownload}
          className="flex items-center gap-2"
          isLoading={isDownloading}
        >
          {!isDownloading && <LucideDownload className="size-4" />}
          {isDownloading
            ? t.loadStatusTransition.wizard.actions.downloadingInvoice
            : t.loadStatusTransition.wizard.actions.download}
        </Button>

        <Button
          variant="outline"
          onPress={handleSendEmail}
          className="flex items-center gap-2"
        >
          <LucideMail className="size-4" />
          {t.loadStatusTransition.wizard.actions.sendViaEmail}
        </Button>
      </div>
    </div>
  );

  const renderStepContent = () => {
    if (isReconciled) {
      return renderSuccessScreen();
    }

    const actualStep = skipMaterialsReconciliation
      ? currentStep + 1
      : currentStep;

    switch (actualStep) {
      case 1:
        return load ? (
          <MaterialsReconciliation
            load={load as unknown as LoadRecord}
            deductionTypes={deductionTypes ?? []}
            weightUnit={weightUnit}
            facilityDefaultUnit={facilityDefaultUnit}
            onWeightUnitChange={setWeightUnit}
            className="border-none p-0"
          />
        ) : null;
      case 2:
        return (
          <ReceivingTicketInfo reconciliationId={loadReconciliation?.id} />
        );
      case 3:
        return <InvoiceDetails />;
      default:
        return null;
    }
  };

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={isReconciled ? handleCloseSuccess : handleClose}
      isDismissable={false}
      className="@LoadReconciliationWizard"
    >
      <DialogContent
        position="center"
        dialogClassName="grid-rows-[auto_auto_1fr_auto] bg-[#F8F8F9] rounded-lg"
      >
        <DialogHeader>
          <div className="text-xl font-semibold">
            {isReconciled
              ? t.loadStatusTransition.wizard.completeTitle
              : t.loadStatusTransition.wizard.title(loadNumber ?? '')}
          </div>
        </DialogHeader>

        {/* Step Indicator - hide when reconciled */}
        {!isReconciled && (
          <div className="px-4 pt-0 pb-3">
            <StepIndicator steps={wizardSteps} currentStep={currentStep} />
          </div>
        )}

        <DialogMain className="h-[70vh] w-[1195px] overflow-y-auto bg-white px-5">
          {renderStepContent()}
        </DialogMain>

        <DialogFooter className="rounded-b-lg bg-white">
          {isReconciled ? (
            <div className="flex w-full justify-end">
              <Button
                color="primary"
                variant="solid"
                onPress={handleCloseSuccess}
              >
                {t.loadStatusTransition.wizard.actions.close}
              </Button>
            </div>
          ) : (
            <div className="flex w-full items-center justify-between gap-4">
              <div>
                {!isFirstStep && (
                  <Button
                    color="neutral"
                    variant="outline"
                    onPress={handleBack}
                    isPending={isSubmitting}
                  >
                    {t.bookingsView.wizard.buttons.back}
                  </Button>
                )}
              </div>

              {load && (
                <ReconciliationSummary
                  weightUnit={weightUnit}
                  facilityDefaultUnit={facilityDefaultUnit}
                />
              )}

              <div className="flex gap-3">
                {isLastStep ? (
                  <Button
                    color="primary"
                    variant="solid"
                    onPress={handleReconcile}
                    isPending={isSubmitting}
                    isLoading={isSubmitting}
                  >
                    {t.loadStatusTransition.reconciliation.reconcile}
                  </Button>
                ) : (
                  <Button
                    color="primary"
                    variant="solid"
                    onPress={handleNext}
                    isPending={isSubmitting}
                  >
                    {t.bookingsView.wizard.buttons.next}
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
